import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, DollarSign, MapPin, TrendingUp, Filter, Clock, Users,
  ArrowUpDown, Search, Bookmark, BookmarkCheck, ChevronRight, Shield
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { FundingProgressBar } from "../components/shared/OSComponents";
import { LuxuryBackground, Card, PageHero } from "../components/shared/UIComponents";
import { fmtUSD } from "../utils/currency";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial" };

export default function InvestorMarketplacePage() {
  const { user } = useAuth();
  const { navigateToEntity } = usePageNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ loan_type: "", state: "", sort_by: "newest" });
  const [search, setSearch] = useState("");
  const [watchedIds, setWatchedIds] = useState(new Set());
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.loan_type) params.set("loan_type", filters.loan_type);
    if (filters.state) params.set("state", filters.state);
    params.set("sort_by", filters.sort_by);

    Promise.all([
      axios.get(`${API}/marketplace/opportunities?${params}`, { headers }),
      axios.get(`${API}/watchlist`, { headers }).catch(() => ({ data: { items: [] } })),
    ]).then(([oppRes, wlRes]) => {
      setData(oppRes.data);
      setWatchedIds(new Set(wlRes.data.items.filter(w => w.entity_type === "opportunity").map(w => w.entity_id)));
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(fetchData, [filters]);

  const toggleWatch = async (oppId) => {
    const isWatched = watchedIds.has(oppId);
    try {
      if (isWatched) {
        await axios.delete(`${API}/watchlist/opportunity/${oppId}`, { headers });
        setWatchedIds(prev => { const n = new Set(prev); n.delete(oppId); return n; });
      } else {
        await axios.post(`${API}/watchlist?entity_type=opportunity&entity_id=${oppId}`, {}, { headers });
        setWatchedIds(prev => new Set([...prev, oppId]));
      }
    } catch {}
  };

  const filtered = useMemo(() => {
    if (!data?.opportunities) return [];
    if (!search) return data.opportunities;
    const q = search.toLowerCase();
    return data.opportunities.filter(o =>
      (o.property_address || "").toLowerCase().includes(q) ||
      (o.loan_type_label || "").toLowerCase().includes(q) ||
      (o.state || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  return (
    <div className="relative" data-testid="investor-marketplace-page">
      <PageHero eyebrow="Capital Marketplace" title="Investment Opportunities"
        description="Discover vetted private credit opportunities. Deploy capital into performing real estate loans." />
      <div className="relative">
        <LuxuryBackground />
        <section className="relative z-10 mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">

          {/* Summary Stats */}
          {data?.summary && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: "Open Opportunities", value: data.total, icon: TrendingUp, color: "text-emerald-600" },
                { label: "Target Capital", value: `$${(data.summary.total_target / 1e6).toFixed(1)}M`, icon: DollarSign, color: "text-gold-dark" },
                { label: "Capital Raised", value: `$${(data.summary.total_raised / 1e6).toFixed(1)}M`, icon: DollarSign, color: "text-blue-600" },
                { label: "Avg Yield", value: data.summary.avg_yield ? `${data.summary.avg_yield}%` : "—", icon: TrendingUp, color: "text-amber-600" },
              ].map((s, i) => (
                <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                  <Card hover={false}>
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-gold/10 p-2.5 border border-cream-warm"><s.icon className={`h-5 w-5 ${s.color}`} /></div>
                      <div>
                        <div className="text-2xl font-semibold text-[#0A0A0A]">{s.value}</div>
                        <div className="text-xs text-[#888]">{s.label}</div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-6 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#999]" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search opportunities..."
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-cream-warm bg-white text-sm focus:outline-none focus:border-emerald-300"
                data-testid="marketplace-search" />
            </div>
            {data?.filters?.loan_types?.length > 0 && (
              <select value={filters.loan_type} onChange={e => setFilters(f => ({ ...f, loan_type: e.target.value }))}
                className="px-3 py-2.5 rounded-xl border border-cream-warm bg-white text-sm" data-testid="filter-loan-type">
                <option value="">All Types</option>
                {data.filters.loan_types.map(t => <option key={t} value={t}>{LOAN_LABELS[t] || t}</option>)}
              </select>
            )}
            {data?.filters?.states?.length > 0 && (
              <select value={filters.state} onChange={e => setFilters(f => ({ ...f, state: e.target.value }))}
                className="px-3 py-2.5 rounded-xl border border-cream-warm bg-white text-sm" data-testid="filter-state">
                <option value="">All States</option>
                {data.filters.states.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            <select value={filters.sort_by} onChange={e => setFilters(f => ({ ...f, sort_by: e.target.value }))}
              className="px-3 py-2.5 rounded-xl border border-cream-warm bg-white text-sm" data-testid="filter-sort">
              <option value="newest">Newest First</option>
              <option value="yield_high">Highest Yield</option>
              <option value="yield_low">Lowest Yield</option>
              <option value="raise_high">Largest Raise</option>
              <option value="raise_low">Smallest Raise</option>
            </select>
          </div>

          {/* Listings */}
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
          ) : filtered.length === 0 ? (
            <Card hover={false}>
              <div className="text-center py-12 text-[#999]">
                <Shield className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="text-lg mb-2">No opportunities available</p>
                <p className="text-sm">New investment opportunities are added as deals progress through the pipeline.</p>
              </div>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {filtered.map((opp, i) => (
                <motion.div key={opp.opportunity_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <OpportunityCard opp={opp} watched={watchedIds.has(opp.opportunity_id)}
                    onToggleWatch={() => toggleWatch(opp.opportunity_id)}
                    onNavigate={() => navigateToEntity("opportunity", opp.opportunity_id)} />
                </motion.div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function OpportunityCard({ opp, watched, onToggleWatch, onNavigate }) {
  return (
    <Card hover={true} data-testid={`opp-card-${opp.opportunity_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onNavigate}>
          <div className="text-sm font-medium text-[#0A0A0A] truncate">{opp.title || opp.property_address || "Investment Opportunity"}</div>
          <div className="flex gap-2 mt-1 flex-wrap">
            <span className="px-2 py-0.5 rounded-md text-[10px] bg-emerald-100 text-emerald-700 font-bold">{opp.loan_type_label || opp.loan_type}</span>
            {opp.state && <span className="text-xs text-[#999] flex items-center gap-1"><MapPin className="h-3 w-3" />{opp.state}</span>}
          </div>
        </div>
        <button onClick={onToggleWatch} data-testid={`watch-${opp.opportunity_id}`}
          className={`p-1.5 rounded-lg transition-colors ${watched ? "text-amber-500 bg-amber-50" : "text-[#ccc] hover:text-amber-400"}`}>
          {watched ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
        </button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        <div className="rounded-lg bg-cream border border-cream-warm p-2 text-center">
          <div className="text-sm font-semibold text-gold-dark">{opp.projected_yield ? `${opp.projected_yield}%` : "—"}</div>
          <div className="text-[10px] text-[#999]">Projected Yield</div>
        </div>
        <div className="rounded-lg bg-cream border border-cream-warm p-2 text-center">
          <div className="text-sm font-semibold text-[#0A0A0A]">{fmtUSD(opp.target_raise)}</div>
          <div className="text-[10px] text-[#999]">Target Raise</div>
        </div>
        <div className="rounded-lg bg-cream border border-cream-warm p-2 text-center">
          <div className="text-sm font-semibold text-[#0A0A0A]">{opp.projected_term_months || "—"}<span className="text-[10px] text-[#999]">mo</span></div>
          <div className="text-[10px] text-[#999]">Term</div>
        </div>
      </div>

      {/* Funding Progress */}
      <FundingProgressBar raised={opp.raised} target={opp.target_raise} label="Funding Progress" />

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 mt-3 border-t border-cream-warm">
        <div className="flex items-center gap-3 text-[10px] text-[#888]">
          <span className="flex items-center gap-1"><Users className="h-3 w-3" />{opp.investor_count} investor{opp.investor_count !== 1 ? "s" : ""}</span>
          <span className="flex items-center gap-1"><DollarSign className="h-3 w-3" />Min {fmtUSD(opp.min_investment)}</span>
        </div>
        <div className="flex items-center gap-2">
          {opp.has_committed && <span className="text-[10px] text-emerald-600 font-semibold">Committed</span>}
          <button onClick={onNavigate} data-testid={`view-opp-${opp.opportunity_id}`}
            className="px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-800 transition-colors flex items-center gap-1">
            View Details <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </Card>
  );
}
